import * as React from 'react'
import PropType from 'prop-types'
import { AnimatePresence, motion } from 'framer-motion'

import * as Common from '@rushplay/common'
import css from '@styled-system/css'
import styled from '@emotion/styled'

import * as Icons from '../icons'

const Overlay = styled(motion.div)`
  ${css({
    zIndex: 999,
  })};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
`

const Wrapper = styled(motion.div)`
  ${css({
    width: ['100%', '375px'],
    background: 'linear-gradient(#0d111d, #0c101b)',
    zIndex: '999999999999',
    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.5)',
  })};
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  line-height: 1.25;
  color: white;
`

/**
 * React component wrapping the Fluid Payments `<fluid-widget>` web component.
 *
 * It exposes event handlers for Fluid widget events:
 * - `fluid-command`
 * - `fluid-info`
 * - `fluid-error`
 *
 * Use this component to embed the Fluid widget and handle its lifecycle events.
 *
 * @component
 *
 * @param {object} props - Component props
 * @param {string} [props.transaction] - Transaction ID string to pass to widget
 * @param {boolean} [props.open=false] - Whether the widget is open
 * @param {function} [props.onCommand] - Handler for `fluid-command` events
 * @param {function} [props.onInfo] - Handler for `fluid-info` events
 * @param {function} [props.onError] - Handler for `fluid-error` events
 * @param {function} [props.onClose] - Handler for closing the widget
 * @param {string | number} [props.balance] - Balance in cents
 * @param {string} [props.withdrawableBalance] - Withdrawable balance in cents
 * @param {string} [props.depositLimit] - Deposit limit in cents
 * @param {string} [props.successCtaLink] - Success CTA link
 * @param {string} [props.bonuses] - stringified array of bonuses
 * @param {string} [props.seonSession] - SEON session ID for fraud prevention
 * @param {string} [props.withdrawalWarning] - Stringified JSON {title, message} shown on withdrawal screen
 *
 * @example
 * // Example usage:
 * function handleCommand(event) {
 *   console.log('Command event received:', event.detail)
 * }
 *
 * function handleInfo(event) {
 *   console.log('Info event received:', event.detail)
 * }
 *
 * function handleError(event) {
 *   console.error('Error event received:', event.detail)
 * }
 *
 * function handleClose() {
 * console.log('Widget closed')
 * }
 *
 *
 * <FluidInitialised
 *   transaction="abc123"
 *   open={true}
 *   onCommand={handleCommand}
 *   onInfo={handleInfo}
 *   onError={handleError}
 *   onClose={handleClose}
 *   balance="1000"
 *   withdrawableBalance="500"
 *   depositLimit="2000"
 *   successCtaLink="https://example.com/success"
 * />
 */
export function FluidInitialised(props) {
  const ref = React.useRef(null)

  React.useLayoutEffect(() => {
    if (!ref.current) {
      return
    }

    const fluid = ref.current

    if (props.onCommand) {
      fluid.addEventListener('fluid-command', props.onCommand)
    }
    if (props.onInfo) {
      fluid.addEventListener('fluid-info', props.onInfo)
    }
    if (props.onError) {
      fluid.addEventListener('fluid-error', props.onError)
    }

    return () => {
      if (props.onCommand) {
        fluid.removeEventListener('fluid-command', props.onCommand)
      }
      if (props.onInfo) {
        fluid.removeEventListener('fluid-info', props.onInfo)
      }
      if (props.onError) {
        fluid.removeEventListener('fluid-error', props.onError)
      }
    }
  }, [props.onCommand, props.onInfo, props.onError])

  return (
    <AnimatePresence>
      {props.open && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={props.onClose}
          />

          <Wrapper
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100, transition: { duration: 0.2 } }}
            transition={{
              x: { type: 'spring', stiffness: 200, damping: 25 },
              default: { duration: 0.3 },
            }}
            key={'fluid-drawer'}
          >
            <Common.Box
              widht="100%"
              height="30px"
              display="flex"
              justifyContent="flex-end"
              px={1}
              alignItems="center"
              backgroundColor="#26132f"
            >
              <Common.Box onClick={props.onClose} cursor="pointer">
                <Icons.Clear />
              </Common.Box>
            </Common.Box>
            <fluid-widget
              ref={ref}
              id="fluid-widget"
              transaction={props.transaction}
              open={props.open}
              balance={props.balance}
              withdrawable-balance={props.withdrawableBalance}
              transaction-attributes={JSON.stringify({
                seonSession: props.seonSession || '',
              })}
              deposit-limit={props.depositLimit || ''}
              success-cta-link={props.successCtaLink || ''}
              bonuses={props.bonuses}
              withdrawal-warning={props.withdrawalWarning || ''}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </Wrapper>
        </>
      )}
    </AnimatePresence>
  )
}

FluidInitialised.propTypes = {
  transaction: PropType.string,
  open: PropType.bool,
  onCommand: PropType.func,
  onInfo: PropType.func,
  onError: PropType.func,
  onClose: PropType.func,
  balance: PropType.string,
  withdrawableBalance: PropType.string,
  depositLimit: PropType.string,
  successCtaLink: PropType.string,
  bonuses: PropType.string,
  seonSession: PropType.string,
  withdrawalWarning: PropType.string,
}

// for lazy loading
export default FluidInitialised
